import type { IconName } from '../components/icon'

/**
 * The navigation — all seventeen entries, in the design's order.
 *
 * Five have live API routes today. The other twelve are built as real screens with the real
 * layout, and each says plainly that its endpoint does not exist yet rather than showing invented
 * numbers. The distinction is `wired`: it drives an honest banner on the screen, never a missing
 * entry in the sidebar.
 */
export type Route =
  | 'overview'
  | 'mailboxes'
  | 'replies'
  | 'campaigns'
  | 'contacts'
  | 'domains'
  | 'sequences'
  | 'templates'
  | 'warmup'
  | 'deliverability'
  | 'schedules'
  | 'suppressions'
  | 'observability'
  | 'alerts'
  | 'audit'
  | 'webhooks'
  | 'platform'
  | 'settings'

export interface NavEntry {
  id: Route
  label: string
  icon: IconName
  /** Whether an API route exists behind it. */
  wired: boolean
  /** The architecture section this screen implements, shown on the unwired ones. */
  section?: string
  /**
   * Platform plane — Axmion's own, not any tenant's (P027).
   *
   * Hidden from the sidebar for anyone whose `platformRole` is NONE, which is every customer.
   * **This is presentation, not security**: both screens behind it refuse on their own, and the
   * endpoints they call are guarded by `@PlatformRoute()` on the server. Hiding a control whose
   * only protection is that it is hidden is how a console teaches somebody the wrong lesson.
   */
  platformOnly?: boolean
}

export const NAV: NavEntry[] = [
  { id: 'overview', label: 'Overview', icon: 'overview', wired: true },
  { id: 'mailboxes', label: 'Mailboxes', icon: 'mailboxes', wired: true, section: '§20, §54' },
  { id: 'replies', label: 'Replies', icon: 'replies', wired: true, section: '§36, §37, §59' },
  { id: 'campaigns', label: 'Campaigns', icon: 'campaigns', wired: true, section: '§45, §81, §99' },
  { id: 'contacts', label: 'Contacts', icon: 'contacts', wired: true },
  { id: 'domains', label: 'Domains', icon: 'domains', wired: true },
  { id: 'sequences', label: 'Sequences', icon: 'sequences', wired: false, section: '§22' },
  { id: 'templates', label: 'Templates', icon: 'templates', wired: true, section: '§28, §64' },
  { id: 'warmup', label: 'Warmup', icon: 'warmup', wired: false, section: '§20' },
  {
    id: 'deliverability',
    label: 'Deliverability',
    icon: 'deliverability',
    wired: true,
    section: '§16, §35',
  },
  { id: 'schedules', label: 'Schedules', icon: 'schedules', wired: true, section: '§21' },
  { id: 'suppressions', label: 'Suppressions', icon: 'suppressions', wired: true },
  { id: 'observability', label: 'Observability', icon: 'observability', wired: false, section: '§69' },
  { id: 'alerts', label: 'Alerts', icon: 'alerts', wired: false, section: '§71' },
  { id: 'audit', label: 'Audit log', icon: 'audit', wired: true, section: '§57' },
  // Both of these are the platform plane, and both were reachable only by `curl` until
  // 2026-08-22. Webhooks waited on this surface rather than on its endpoint: `webhook_events` has
  // no `organizationId`, because a delivery arrives before anything is known about whose it is.
  {
    id: 'webhooks',
    label: 'Webhooks',
    icon: 'webhooks',
    wired: true,
    section: '§50, §51',
    platformOnly: true,
  },
  { id: 'platform', label: 'Platform', icon: 'platform', wired: true, section: '§47', platformOnly: true },
  { id: 'settings', label: 'Settings', icon: 'settings', wired: true },
]

export const BY_ID = Object.fromEntries(NAV.map((entry) => [entry.id, entry])) as Record<
  Route,
  NavEntry
>

export const TITLES = Object.fromEntries(NAV.map((entry) => [entry.id, entry.label])) as Record<
  Route,
  string
>
